import { DOCUMENT, Injectable, inject, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { CHATBOT_CONFIG } from "../config";
import { ChatbotDomainLogin, Domain } from "../models/domain";
import { IdentityService } from "./identity.service";

/** The field of the answer that holds the token, unless the host names another one. */
const DEFAULT_TOKEN_FIELD = "sessionToken";

/**
 * The password in the shape every login endpoint expects: base64. One shape for all of them, so
 * that this library never has to know which domain wants which. `btoa` alone throws on anything
 * outside Latin 1, so the text is turned into bytes first and those bytes are what gets encoded.
 */
function encodePassword(password: string): string {
    const bytes = new TextEncoder().encode(password);
    return btoa(String.fromCharCode(...bytes));
}

/**
 * Signs a tester in as a user of another domain, so an agent can be tried out the way the people
 * it serves would meet it.
 *
 * The credentials are asked for in the browser's own dialog and exchanged **directly with that
 * domain's login endpoint**. Two consequences were the reason for doing it this way:
 *
 * - it works for every domain, including the ones whose users have no web application at all.
 *   A native client reproduces this with a dialog of its own and the same exchange, while
 *   borrowing a web application's sign in page would have been reproducible nowhere else;
 * - nothing here needs to know which domains exist. The endpoint comes from the host's own
 *   configuration, so this library stays ignorant of the products it serves.
 *
 * The request deliberately does not go through the shared `HttpClient`. A host's interceptors run
 * on that client, and this one request carries a password to somebody else's origin: nothing the
 * host attaches to its own traffic belongs on it.
 */
@Injectable({ providedIn: "root" })
export class DomainLoginService {
    private readonly config = inject(CHATBOT_CONFIG);
    private readonly identity = inject(IdentityService);
    private readonly translate = inject(TranslateService);
    private readonly document = inject(DOCUMENT);

    /** True while an exchange is in flight, so the caller can keep the composer waiting. */
    readonly busy = signal(false);

    /**
     * Asks for this domain's credentials and takes its identity on, and answers whether it
     * worked.
     *
     * A cancelled dialog is not a failure: it simply leaves the conversation on the identity it
     * already had, which is the host's own user.
     */
    async signIn(domain: Domain, login: ChatbotDomainLogin): Promise<boolean> {
        const view = this.document.defaultView;
        if (!view) {
            return false;
        }

        const username = view.prompt(this.ask("chatbot.signIn.username", domain));
        if (!username) {
            return false;
        }

        const password = view.prompt(this.ask("chatbot.signIn.password", domain));
        if (!password) {
            return false;
        }

        const body: Record<string, string> = { username, password: encodePassword(password) };
        for (const field of login.extraFields ?? []) {
            const value = view.prompt(this.ask("chatbot.signIn.field", domain, field.name), field.default ?? "");
            if (value === null) {
                return false;
            }
            body[field.name] = value;
        }

        this.busy.set(true);
        try {
            const token = await this.exchange(login, body);
            this.identity.startTestIdentity(domain, token);
            return true;
        } catch (error) {
            console.error(`Sign in as ${domain} failed`, error);
            view.alert(this.ask("chatbot.signIn.failed", domain));
            return false;
        } finally {
            this.busy.set(false);
        }
    }

    /** Back to the host's own user. */
    stop(): void {
        this.identity.clearTestIdentity();
    }

    /**
     * Posts the credentials and returns the token, or throws.
     *
     * `credentials: "omit"` because this is an exchange, not a session: whatever cookies the
     * browser holds for that origin have nothing to do with who is signing in here, and sending
     * them would need the endpoint to allow credentialed cross origin requests for no reason.
     */
    private async exchange(login: ChatbotDomainLogin, body: Record<string, string>): Promise<string> {
        const response = await fetch(login.url, {
            method: "POST",
            credentials: "omit",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`Login endpoint answered ${response.status}`);
        }

        const payload: unknown = await response.json();
        const field = login.tokenField ?? DEFAULT_TOKEN_FIELD;
        const token = (payload as Record<string, unknown> | null)?.[field];
        if (typeof token !== "string" || token === "") {
            throw new Error(`Login endpoint answered without a "${field}"`);
        }

        return token;
    }

    /** One line of dialog text, with the domain the tester is signing into named in it. */
    private ask(key: string, domain: Domain, field?: string): string {
        return this.translate.instant(key, { domain, field });
    }

    /** Whether this domain can be signed into at all, which is the host's decision. */
    loginFor(domain: Domain | null): ChatbotDomainLogin | undefined {
        return domain ? this.config.domains[domain]?.login : undefined;
    }
}
